import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/store";
import { fetchCommonPageProps } from "declarations/common";
import {
  DeleteMessageProps,
  GetConversationProps,
  GetConversationsProps,
  GetMessagesProps,
  MarkConversationReadProps,
  SendDirectMessageProps,
  SendMessageProps,
} from "declarations/chatServices";
import {
  DeleteMessage,
  GetConversation,
  GetConversations,
  GetMessages,
  MarkConversationRead,
  SendDirectMessage,
  SendMessage,
} from "api/services/chat";
import {
  removeMessage,
  setConversationRead,
  setConversations,
  setMessageCount,
  setMessages,
  upsertConversation,
  upsertMessage,
} from "reducers/chatReducer";
import { useAsyncOperation } from "./useAsyncOperation";

export default function useChat() {
  const { execute } = useAsyncOperation();
  const dispatch = useDispatch();
  const userRole = String(
    useSelector((state: RootState) => state.user.user?.role || "")
  ).toLowerCase();
  const { chat, conversations, messages } = useSelector(
    (state: RootState) => state.chat
  );

  const getConversation = async (
    { id, setLoading }: GetConversationProps,
    { reload }: { reload?: boolean } = {}
  ) => {
    if (chat?._id === id && chat?.customer && !reload) {
      return chat;
    }

    try {
      const response = await execute(() => GetConversation({ id, setLoading }), {
        errorMessage: "Fetching Conversation Failed",
      });

      const { conversation, message, status } = response;
      if (status !== 200) throw new Error(message);

      if (conversation) {
        dispatch(upsertConversation({ ...conversation }));
      }

      return conversation;
    } catch (error) {
      return null;
    }
  };

  const getConversations = async (
    query: GetConversationsProps,
    { local, more, reload }: fetchCommonPageProps
  ) => {
    if (conversations && conversations.length > 0 && !local && !more && !reload)
      return conversations;

    try {
      const response = await execute(() => GetConversations({ ...query }), {
        errorMessage: "Fetching Conversations Failed",
      });

      const { conversations: loadedConversations, message, status } = response;
      if (status !== 200) throw new Error(message);

      let totalConversations = [...loadedConversations];
      if (local) return totalConversations;
      if (more) totalConversations = [...conversations, ...loadedConversations];

      dispatch(setConversations(totalConversations));
      return totalConversations;
    } catch (error) {
      throw error;
    }
  };

  const getMessages = async (
    query: GetMessagesProps,
    {
      local,
      more,
      reload,
    }: { local?: boolean; more?: boolean; reload?: boolean } = {}
  ) => {
    if (
      messages &&
      messages.length > 0 &&
      !local &&
      !more &&
      !reload &&
      chat?._id === query.id
    ) {
      return messages;
    }

    try {
      const response = await execute(() => GetMessages({ ...query }), {
        errorMessage: "Fetching Messages Failed",
      });

      const {
        messages: loadedMessages,
        message,
        status,
        count,
      } = response;
      if (status !== 200) throw new Error(message);

      if (typeof count === "number") {
        dispatch(setMessageCount(count));
      }

      let totalMessages = [...loadedMessages];
      if (more) totalMessages = [...messages, ...loadedMessages];
      if (local) return totalMessages;

      dispatch(setMessages(totalMessages));
      return totalMessages;
    } catch (error) {
      return null;
    }
  };

  const sendMessage = async (query: SendMessageProps) => {
    try {
      const response = await execute(() => SendMessage({ ...query }), {
        errorMessage: "Sending Message Failed",
        showProgress: false,
      });

      const { newMessage, conversation, message, status } = response;
  
      if (status !== 201) throw new Error(message);

      if (conversation) {
        dispatch(upsertConversation({ ...conversation }));
      }

      if (newMessage) {
        dispatch(
          upsertMessage({
            ...newMessage,
            status: "sent",
          })
        );
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  const sendDirectMessage = async ({
    customerId,
    text,
    car,
    setLoading,
  }: SendDirectMessageProps) => {
    try {
      const response = await execute(
        () => SendDirectMessage({ customerId, text, car, setLoading }),
        {
          errorMessage: "Sending Message Failed",
          showProgress: false,
        }
      );

      const { newMessage, conversation, message, status } = response;

      if (status !== 201) throw new Error(message);

      if (conversation) {
        dispatch(upsertConversation({ ...conversation }));
      }

      if (newMessage) {
        dispatch(
          upsertMessage({
            ...newMessage,
            status: "sent",
          })
        );
      }

      return conversation;
    } catch (error) {
      return null;
    }
  };

  const markConversationRead = async ({
    id,
    setLoading,
  }: MarkConversationReadProps) => {
    try {
      const response = await execute(
        () => MarkConversationRead({ id, setLoading }),
        {
          errorMessage: "Updating Messages Failed",
          showProgress: false,
        }
      );

      const { conversation, message, status } = response;
      if (status !== 200) throw new Error(message);

      if (conversation) {
        dispatch(upsertConversation({ ...conversation, moveToTop: false }));
      } else {
        const unreadRole =
          userRole === "admin"
            ? "admin"
            : userRole === "staff"
            ? "staff"
            : "customer";

        dispatch(
          setConversationRead({
            conversationId: id,
            role: unreadRole,
          })
        );
      }

      return conversation;
    } catch (error) {
      return null;
    }
  };

  const deleteMessage = async ({ id, setLoading }: DeleteMessageProps) => {
    try {
      const response = await execute(() => DeleteMessage({ id, setLoading }), {
        errorMessage: "Deleting Message Failed",
        showProgress: false,
      });

      const { conversation, deletedMessageId, message, status } = response;
      if (status !== 200) throw new Error(message);

      dispatch(removeMessage({ messageId: String(deletedMessageId || id) }));

      if (conversation) {
        dispatch(upsertConversation({ ...conversation, moveToTop: false }));
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  return {
    getConversation,
    getConversations,
    getMessages,
    sendMessage,
    sendDirectMessage,
    markConversationRead,
    deleteMessage,
  };
}
